import React, { useEffect, useState, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Building2, TriangleAlert as AlertTriangle, RefreshCw } from 'lucide-react';
import { Toaster } from 'sonner';
import { supabase } from './lib/supabase';
import { useUserType } from './hooks/useUserType';
import { ErrorBoundary } from './components/ErrorBoundary';
import { OfflineBanner } from './components/OfflineBanner';
import { SkeletonDashboard, SkeletonList } from './components/SkeletonLoaders';
import Sidebar from './components/Sidebar';
import ScrollToTop from './components/ScrollToTop';
import NotificationBell from './components/NotificationBell';

const lazyWithRetry = (importFn: () => Promise<any>) =>
  lazy(() =>
    importFn().catch(() =>
      new Promise<any>(resolve => setTimeout(() => resolve(importFn()), 800))
    )
  );

const CommunityDashboard = lazyWithRetry(() => import('./components/CommunityDashboard'));
const FinancesPage = lazyWithRetry(() => import('./components/FinancesPage'));
const CorpusFundPage = lazyWithRetry(() => import('./components/CorpusFundPage'));
const TaxesPage = lazyWithRetry(() => import('./components/TaxesPage'));
const MaintenancePage = lazyWithRetry(() => import('./components/MaintenancePage'));
const EquipmentPage = lazyWithRetry(() => import('./components/EquipmentPage'));
const PropertiesPage = lazyWithRetry(() => import('./components/PropertiesPage'));
const MeetingsPage = lazyWithRetry(() => import('./components/MeetingsPage'));
const NotesPage = lazyWithRetry(() => import('./components/NotesPage'));
const ContactsPage = lazyWithRetry(() => import('./components/ContactsPage'));
const AuthPage = lazyWithRetry(() => import('./components/AuthPage'));
const LandingPage = lazyWithRetry(() => import('./components/LandingPage'));
const FeaturesPage = lazyWithRetry(() => import('./components/FeaturesPage'));
const BlogsPage = lazyWithRetry(() => import('./components/BlogsPage'));
const SettingsPage = lazyWithRetry(() => import('./components/SettingsPage'));
const UserTypeSelector = lazyWithRetry(() => import('./components/UserTypeSelector'));
const ActivityLog = lazyWithRetry(() => import('./components/ActivityLog'));
const NoticeBoardPage = lazyWithRetry(() => import('./components/NoticeBoardPage'));
const AssociationRulesPage = lazyWithRetry(() => import('./components/AssociationRulesPage'));
const PropertyMembersPage = lazyWithRetry(() => import('./components/PropertyMembersPage'));
const InvitationAcceptPage = lazyWithRetry(() => import('./components/InvitationAcceptPage'));
const EmailVerificationPage = lazyWithRetry(() => import('./components/EmailVerificationPage'));
const HowToUseGuidePage = lazyWithRetry(() => import('./components/HowToUseGuidePage'));
const CommunityCalendarPage = lazyWithRetry(() => import('./components/CommunityCalendarPage'));
const CommunityMarketplacePage = lazyWithRetry(() => import('./components/CommunityMarketplacePage'));
const ComponentShowcase = lazyWithRetry(() => import('./components/ComponentShowcase'));
const RemindersPage = lazyWithRetry(() => import('./components/RemindersPage'));
const ComplaintsPage = lazyWithRetry(() => import('./components/ComplaintsPage'));
const DocumentTemplatesPage = lazyWithRetry(() => import('./components/DocumentTemplatesPage'));
const FacilityBookingPage = lazyWithRetry(() => import('./components/FacilityBookingPage'));
const VisitorManagementPage = lazyWithRetry(() => import('./components/VisitorManagementPage'));
const CommunityFeedPage = lazyWithRetry(() => import('./components/CommunityFeedPage'));
const GuardsPage = lazyWithRetry(() => import('./components/GuardsPage'));
const LawnManagementPage = lazyWithRetry(() => import('./components/LawnManagementPage'));
const VendorManagementPage = lazyWithRetry(() => import('./components/VendorManagementPage'));
const OneOffWorkersPage = lazyWithRetry(() => import('./components/OneOffWorkersPage'));
const PrivacyPolicyPage = lazyWithRetry(() => import('./components/PrivacyPolicyPage'));
const AGMReportPage = lazyWithRetry(() => import('./components/AGMReportPage'));
const ResidentDuesPage = lazyWithRetry(() => import('./components/ResidentDuesPage'));

const routeFallback = (
  <div className="flex flex-col items-center justify-center py-20 px-4 text-center">
    <div className="p-3 bg-red-50 dark:bg-red-500/10 rounded-full mb-4">
      <AlertTriangle className="w-6 h-6 text-red-500" />
    </div>
    <p className="font-semibold text-foreground mb-1">Failed to load this page</p>
    <p className="text-sm text-muted-foreground mb-4">Something went wrong. Please try again.</p>
    <button
      onClick={() => window.location.reload()}
      className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
    >
      <RefreshCw className="w-4 h-4" />
      Refresh page
    </button>
  </div>
);

const PageBoundary: React.FC<{ children: React.ReactNode; skeleton?: React.ReactNode }> = ({
  children,
  skeleton = <div className="p-6"><SkeletonList items={4} /></div>,
}) => (
  <ErrorBoundary fallback={routeFallback}>
    <Suspense fallback={skeleton}>
      {children}
    </Suspense>
  </ErrorBoundary>
);

const AppContent = () => {
  const { userProfile, loading: userLoading, canManageUsers } = useUserType();
  const location = useLocation();
  const [selectedPropertyId, setSelectedPropertyId] = useState<string>('');

  const selectedProperty = userProfile?.properties?.find(p => p.id === selectedPropertyId);

  useEffect(() => {
    // Auto-select first property if user has properties
    if (userProfile?.properties?.length && !selectedPropertyId) {
      setSelectedPropertyId(userProfile.properties[0].id);
    }
  }, [userProfile?.properties, selectedPropertyId]);

  // Check if on invitation page
  if (location.pathname === '/invite') {
    return <InvitationAcceptPage />;
  }

  if (userLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  // If user doesn't have a user_type set, show the selector
  if (userProfile && !userProfile.user_type && location.pathname !== '/settings') {
    return (
      <div className="min-h-screen bg-muted">
        <UserTypeSelector />
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-muted">
      <a href="#main-content" className="skip-link">
        Skip to main content
      </a>
      <Sidebar />
      <main id="main-content" className="flex-1 overflow-x-hidden overflow-y-auto relative flex flex-col">
        {/* Top bar with notification bell */}
        <div className="sticky top-0 z-30 flex justify-end px-4 py-2.5 bg-background/80 backdrop-blur-sm border-b border-border">
          <NotificationBell />
        </div>

        {/* Property Selector Bar */}
        {userProfile?.properties && userProfile.properties.length > 1 &&
         location.pathname !== '/' &&
         location.pathname !== '/properties' && (
          <div className="bg-card border-b border-border p-3 sm:p-4">
            <div className="max-w-7xl mx-auto px-4 sm:px-0">
              <label htmlFor="property-selector" className="block text-xs sm:text-sm font-medium text-foreground mb-2">
                Current Property
              </label>
              <select
                id="property-selector"
                value={selectedPropertyId}
                onChange={(e) => setSelectedPropertyId(e.target.value)}
                className="block w-full sm:max-w-md rounded-md border-border shadow-sm focus:border-primary focus:ring-primary text-sm bg-background text-foreground"
              >
                {userProfile.properties.map((property) => (
                  <option key={property.id} value={property.id}>
                    {property.name} ({property.role})
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}

        <div className="flex-1">
          <Routes>
            <Route path="/" element={<Navigate to="/community" replace />} />
            <Route path="/community" element={<PageBoundary skeleton={<div className="p-6"><SkeletonDashboard /></div>}><CommunityDashboard /></PageBoundary>} />
            <Route path="/properties" element={<PageBoundary><PropertiesPage /></PageBoundary>} />
            <Route path="/members" element={<PageBoundary><PropertyMembersPage /></PageBoundary>} />
            <Route path="/finances" element={<PageBoundary skeleton={<div className="p-6"><SkeletonDashboard /></div>}><FinancesPage propertyId={selectedPropertyId} /></PageBoundary>} />
            <Route path="/corpus-fund" element={<PageBoundary><CorpusFundPage propertyId={selectedPropertyId} /></PageBoundary>} />
            <Route path="/agm-report" element={<PageBoundary><AGMReportPage propertyId={selectedPropertyId} /></PageBoundary>} />
            <Route path="/my-dues" element={<PageBoundary><ResidentDuesPage propertyId={selectedPropertyId} /></PageBoundary>} />
            <Route path="/taxes" element={<PageBoundary skeleton={<div className="p-6"><SkeletonDashboard /></div>}><TaxesPage propertyId={selectedPropertyId} propertyType={selectedProperty?.property_type} /></PageBoundary>} />
            <Route path="/reminders" element={<PageBoundary><RemindersPage propertyId={selectedPropertyId} /></PageBoundary>} />
            <Route path="/maintenance" element={<PageBoundary><MaintenancePage propertyId={selectedPropertyId} /></PageBoundary>} />
            <Route path="/equipment" element={<PageBoundary><EquipmentPage propertyId={selectedPropertyId} /></PageBoundary>} />
            <Route path="/meetings" element={<PageBoundary><MeetingsPage propertyId={selectedPropertyId} /></PageBoundary>} />
            <Route path="/notes" element={<PageBoundary><NotesPage propertyId={selectedPropertyId} /></PageBoundary>} />
            <Route path="/contacts" element={<PageBoundary><ContactsPage propertyId={selectedPropertyId} /></PageBoundary>} />
            <Route path="/notices" element={<PageBoundary><NoticeBoardPage propertyId={selectedPropertyId} /></PageBoundary>} />
            <Route path="/complaints" element={<PageBoundary><ComplaintsPage propertyId={selectedPropertyId} /></PageBoundary>} />
            <Route path="/rules" element={<PageBoundary><AssociationRulesPage propertyId={selectedPropertyId} /></PageBoundary>} />
            <Route path="/documents" element={<PageBoundary><DocumentTemplatesPage propertyId={selectedPropertyId} /></PageBoundary>} />
            <Route path="/calendar" element={<PageBoundary><CommunityCalendarPage /></PageBoundary>} />
            <Route path="/marketplace" element={<PageBoundary><CommunityMarketplacePage /></PageBoundary>} />
            <Route path="/facility-booking" element={<PageBoundary><FacilityBookingPage propertyId={selectedPropertyId} /></PageBoundary>} />
            <Route path="/visitors" element={<PageBoundary><VisitorManagementPage propertyId={selectedPropertyId} /></PageBoundary>} />
            <Route path="/guards" element={<PageBoundary><GuardsPage propertyId={selectedPropertyId} /></PageBoundary>} />
            <Route path="/feed" element={<PageBoundary><CommunityFeedPage propertyId={selectedPropertyId} /></PageBoundary>} />
            <Route path="/lawn" element={canManageUsers() ? <PageBoundary><LawnManagementPage propertyId={selectedPropertyId} /></PageBoundary> : <Navigate to="/community" replace />} />
            <Route path="/vendors" element={canManageUsers() ? <PageBoundary><VendorManagementPage propertyId={selectedPropertyId} /></PageBoundary> : <Navigate to="/community" replace />} />
            <Route path="/workers" element={canManageUsers() ? <PageBoundary><OneOffWorkersPage propertyId={selectedPropertyId} /></PageBoundary> : <Navigate to="/community" replace />} />
            <Route path="/activity" element={<PageBoundary><ActivityLog propertyId={selectedPropertyId} /></PageBoundary>} />
            <Route path="/guide" element={<PageBoundary><HowToUseGuidePage /></PageBoundary>} />
            <Route path="/components" element={<PageBoundary><ComponentShowcase /></PageBoundary>} />
            <Route path="/settings" element={<PageBoundary><SettingsPage /></PageBoundary>} />
            <Route path="*" element={<Navigate to="/community" replace />} />
          </Routes>
        </div>

        {/* Footer */}
        <footer className="bg-card border-t border-border mt-auto">
          <div className="max-w-7xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <img
                    src="/apartmentwiselogoweb.png"
                    alt="Apartment Wise Logo"
                    className="h-12 w-auto"
                    width="160"
                    height="48"
                  />
                </div>
                <p className="text-muted-foreground max-w-md">
                  Streamline your apartment management with our comprehensive solution.
                </p>
              </div>
              <div>
                <h3 className="text-lg font-semibold mb-4 text-foreground">Support</h3>
                <ul className="space-y-2 text-muted-foreground">
                  <li>
                    <a
                      href="mailto:support@apartmentwise.in"
                      className="hover:text-primary transition-colors"
                    >
                      Contact Support
                    </a>
                  </li>
                </ul>
              </div>
            </div>
            <div className="mt-8 pt-8 border-t border-border text-center text-muted-foreground">
              <p>© 2026 Apartment Wise by Uptownlabs.in. All rights reserved.</p>
            </div>
          </div>
        </footer>
      </main>
      <ScrollToTop />
    </div>
  );
};
function App() {
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const isPasswordRecovery = () => {
      const hashParams = new URLSearchParams(window.location.hash.substring(1));
      return hashParams.get('type') === 'recovery';
    };

    const checkSession = async () => {
      const isVerificationPage = window.location.pathname === '/auth/confirm';

      if (isVerificationPage || isPasswordRecovery()) {
        setSession(null);
        setLoading(false);
        return;
      }

      const { data: { session } } = await supabase.auth.getSession();
      setSession(session);
      setLoading(false);
    };

    checkSession();

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      const isVerificationPage = window.location.pathname === '/auth/confirm';

      if (isVerificationPage || event === 'PASSWORD_RECOVERY') {
        setSession(null);
      } else if (event === 'SIGNED_OUT') {
        setSession(null);
        // Routing handles redirect — no explicit navigate needed
      } else {
        setSession(session);
      }
    });

    // When the tab becomes visible again, browsers may have suspended the
    // auto-refresh timer. Re-check the session so stale tokens are refreshed
    // silently, or the user is redirected cleanly instead of hitting a crash.
    const handleVisibilityChange = async () => {
      if (document.visibilityState !== 'visible') return;
      const { data: { session }, error } = await supabase.auth.getSession();
      if (error || !session) {
        setSession(null);
        if (!window.location.pathname.startsWith('/login')) {
          window.location.href = '/login?reason=expired';
        }
      } else {
        setSession(session);
      }
    };

    // When network reconnects, refresh the session silently so the next
    // API call goes through without a stale token.
    const handleOnline = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session) setSession(session);
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('online', handleOnline);

    return () => {
      subscription.unsubscribe();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('online', handleOnline);
    };
  }, []);


  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <ErrorBoundary>
      <OfflineBanner />
      <Router>
        <Toaster
          position="bottom-center"
          visibleToasts={3}
          toastOptions={{
            className: 'toast-custom',
          }}
        />
        <Suspense fallback={
          <div className="min-h-screen flex items-center justify-center bg-background">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        }>
          <Routes>
            <Route path="/auth/confirm" element={<EmailVerificationPage />} />
            <Route path="/features" element={<FeaturesPage />} />
            <Route path="/blog" element={<BlogsPage />} />
            <Route path="/blog/:slug" element={<BlogsPage />} />
            <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
            {!session ? (
              <>
                <Route path="/" element={<LandingPage />} />
                <Route path="/login" element={<AuthPage />} />
                <Route path="/auth" element={<AuthPage />} />
                <Route path="/invite" element={<InvitationAcceptPage />} />
                <Route path="*" element={<Navigate to="/login" replace />} />
              </>
            ) : (
              <>
                <Route path="/login" element={<AuthPage />} />
                <Route path="/auth" element={<AuthPage />} />
                <Route path="/*" element={<AppContent />} />
              </>
            )}
          </Routes>
          {!session && <ScrollToTop />}
        </Suspense>
      </Router>
    </ErrorBoundary>
  );
}

export default App;